import clsx from 'clsx';
import React from 'react';
import { Status } from '../../../js/api';
import { StatusCodes, WorkflowScopes } from '../constants/workflows';
import { useWorkflowMetadata } from '../hooks/useWorkflowMetadata';

import { getStatusStyle } from '../utils/statusStyles';

interface StatusPillProps {
  scope: WorkflowScopes;
  status: StatusCodes | Status;
  className?: string;
  text?: string;
  small?: boolean;
}

const isStatusObject = (value: StatusCodes | Status): value is Status => {
  return typeof value === 'object' && value !== null && 'code' in value;
};

export const StatusPill: React.FC<StatusPillProps> = ({
  scope,
  status,
  className,
  text,
  small,
}) => {
  const { statuses } = useWorkflowMetadata(scope);

  const resolvedStatus: Status = React.useMemo(() => {
    // If caller already gave us a full Status object, pass it through.
    if (isStatusObject(status)) return status;

    // Otherwise status is a StatusCodes string: look up the full metadata object and normalize to Status.
    const lookedUp = statuses?.[status as string] as unknown as Status | undefined;
    if (lookedUp) {
      return lookedUp as Status;
    }

    // Fallback: still return a Status-shaped object so getStatusStyle has something consistent.
    return {
      code: status,
      title: status,
      category: '',
      category_title: '',
      category_kind: '',
    } as Status;
  }, [status, statuses]);

  const statusClass = getStatusStyle(resolvedStatus);

  return (
    <span
      className={clsx(
        'tw:rounded-full tw:border tw:text-center',
        small ? 'tw:text-[10px]/4 tw:px-[.5rem] tw:py-[.25rem]' : 'tw:text-xs tw:px-2 tw:py-1',
        statusClass,
        className
      )}
    >
      {text || resolvedStatus.title || resolvedStatus.code}
    </span>
  );
};

export default StatusPill;
